
import EventDispatcher from "../../../../util/EventDispatcher";
import { isUndefined } from "../../../../util/TypeUtil";
import StoryPlayerEvent from "../../engine/player/StoryPlayerEvent";
import StoryPlayerFactory from "../../engine/player/StoryPlayerFactory";
import StoryFrameworkPlayerEvent from "./StoryFrameworkPlayerEvent";
import StoryFrameworkPlayerMemento from "./StoryFrameworkPlayerMemento";
import StoryPlayerConditionSolver from "./StoryPlayerConditionSolver";


/**
 * The StoryFrameworkPlayer is a more feature rich player of stories that uses the IStoryPlayer
 * from the Story Engine. On top of the engine player it adds the following features:
 * 
 * <ul>
 *  <li>Clock: the player expects you to pass an IClock to it on which the currentTime can be asked so you don't have to pass this</li>
 *  <li>Conditions: the framework player integrates conditions into the IStoryPlayer so you can use conditions like storynode[123].completed</li>
 *  <li>Timing: the framework player integrates timing conditions</li>
 * </ul>
 * 
 * <p>Note: the StoryFrameworkPlayer is not an extension of the IStoryPlayer but uses a composition structure. This provides
 * the possiblity to have harder typing on our assumptions and provide easier functions.</p>
 * 
 * <p>Providing the extra functionality comes at a price of less flexibility and some restrictions for the other components you use. The Framework
 * player makes the following assumptions:</p>
 * 
 * <ul>
 *   <li>All conditions are of the type logic.LogicalExpression</li>
 *   <li>The data stored at storynodes and arcs must be of type IContent</li>
 * </ul>
 * 
 * @constructor
 * @param {Story} story The Story
 * @param {IClock} clock The IClock
 * @param {Function} solver The solver function to use
 **/
export default class StoryFrameworkPlayer extends EventDispatcher {
    constructor(story, clock, solver) {
        super();

        this._storyPlayer = StoryPlayerFactory.createStoryPlayer(story, this.solve.bind(this));
        this._conditionSolver = new StoryPlayerConditionSolver(this._storyPlayer, solver, clock);
        this._clock = clock;

        this.addEventListeners();
    }

    /**
     * Destroy the IStoryFrameworkPlayer and let it clean itself 
     **/
    destroy() {
        this.removeEventListeners();
        this._storyPlayer = null;
        this._conditionSolver.destroy();
        this._conditionSolver = null;
    }

    /**
     * Start playing the story 
     * @param {number} time The time to set as the start time of the story. Can be used to overwrite the time on the clock. If it is not supplied the currentTime on the clock is used
     **/
    start(time) {
        if (isUndefined(time)) {
            this._storyPlayer.start(this._clock.getCurrentTime());
        } else {
            this._storyPlayer.start(time);
        }
    }

    /**
     * Tells the IStoryFrameworkPlayer that a certain storynode has been completed 
     * @param storyNode The StoryNode that is completed
     * @param result A result object that is stored with the storynode and can be referenced later on
     **/
    complete(storyNode, result) {
        this._storyPlayer.complete(storyNode, this._clock.getCurrentTime(), result);
    }

    /**
     * Let the IStoryFrameworkPlayer evaluate actions.  
     **/
    evaluate() {
        this._storyPlayer.evaluate(this._clock.getCurrentTime());
    }

    /**
     * Set the clock of the IStoryFrameworkPlayer
     * @param {IClock} value The clock instance to use 
     **/
    setClock(value) {
        this._clock = value;
    }

    /**
     * Get the clock of the player 
     * @return The clock of the player
     **/
    getClock() {
        return this._clock;
    }

    /**
     * Solve a variable via the IStoryFrameworkPlayer. This solves the variable with the IStoryFrameworkPlayer variables like storyNode123_completed
     * with. If the variable is not a IStoryFrameworkPlayer variable it is passed to the supplied solve function. 
     * @param {String} name The expression to solve
     **/
    solve(name) {
        return this._conditionSolver.solve(name);
    }

    /**
     * Returns whether the IStoryFrameworkPlayer is started
     * @return True if the IStoryFrameworkPlayer is started, false otherwise
     **/
    isStarted() {
        return this._storyPlayer.isStarted();
    }

    //
    // Functions for getting the current state
    //
    /**
     * Returns an iterator for all the StoryArcs that are unlocked at
     * the current state. Note that no evaluation is done when calling this function.
     * @return {IIterator} An IIterator for all the available StoryArcs 
     **/
    unlockedStoryArcs() {
        return this._storyPlayer.unlockedStoryArcs();
    }

    /**
     * Returns an iterator for all the StoryArcs that are avaialable at
     * the current state. Note that no evaluation is done when calling this function.
     * @return {IIterator} An iterator for all the StoryArcs that are available
     **/
    availableStoryArcs() {
        return this._storyPlayer.availableStoryArcs();
    }

    /**
     * Returns an iterator for all the StoryArcs that are completed at
     * the current state. Note that no evaluation is done when calling this function.
     * @return {IIterator} An iterator for all the completed StoryArcs
     **/
    completedStoryArcs() {
        return this._storyPlayer.completedStoryArcs();
    }

    /**
     * Returns an iterator for all the StoryNodes that are unlocked at
     * the current state. Note that no evaluation is done when calling this function.
     * @return {IIterator} An iterator for all the unlocked StoryNodes 
     **/
    unlockedStoryNodes() {
        return this._storyPlayer.unlockedStoryNodes();
    }

    /**
     * Returns an iterator for all the StoryNodes that are avaialable at
     * the current state. Note that no evaluation is done when calling this function.
     * @return {IIterator} An iterator for all the available StoryNodes 
     **/
    availableStoryNodes() {
        return this._storyPlayer.availableStoryNodes();
    }

    /**
     * Returns an iterator for all the StoryNodes that are completed at
     * the current state. Note that no evaluation is done when calling this function.
     * @return {IIterator} An iterator for all the completed StoryNodes 
     **/
    completedStoryNodes() {
        return this._storyPlayer.completedStoryNodes();
    }

    /**
     * Returns whether a certain StoryArc is unlocked 
     * @param {StoryArc} storyArc 
     * @return {boolean} True if the StoryArc is unlocked, false otherwise
     **/
    isStoryArcUnlocked(storyArc) {
        return this._storyPlayer.isStoryArcUnlocked(storyArc);
    }

    /**
     * Returns whether a certain StoryArc is available  
     * @param {StoryArc} storyArc 
     * @return {boolean}  True if the StoryArc is available, false otherwise
     **/
    isStoryArcAvailable(storyArc) {
        return this._storyPlayer.isStoryArcAvailable(storyArc);
    }

    /**
     * Returns whether a certain StoryArc is completed 
     * @param {StoryArc} storyArc 
     * @return {boolean}  True if the StoryArc is completed, false otherwise
     **/
    isStoryArcCompleted(storyArc) {
        return this._storyPlayer.isStoryArcCompleted(storyArc);
    }

    /**
     * Returns whether a certain StoryNode is unlocked 
     * @param {StoryNode} storyNode
     * @return {boolean}  True if the StoryNode is unlocked, false otherwise
     **/
    isStoryNodeUnlocked(storyNode) {
        return this._storyPlayer.isStoryNodeUnlocked(storyNode);
    }

    /**
     * Returns whether a certain StoryNode is available
     * @param {StoryNode} storyNode 
     * @return {boolean}  True if the StoryNode is available, false otherwise
     **/
    isStoryNodeAvailable(storyNode) {
        return this._storyPlayer.isStoryNodeAvailable(storyNode);
    }

    /**
     * Returns whether a certain StoryNode is completed
     * @param {StoryNode} storyNode 
     * @return {boolean}  True if the StoryNode is completed, false otherwise
     **/
    isStoryNodeCompleted(storyNode) {
        return this._storyPlayer.isStoryNodeCompleted(storyNode);
    }

    /**
     * Returns the time that a StoryNode was unlocked
     * @param {StoryNode} storyNode
     * @return {number}  The value of the time at the unlock of the StoryNode or Infinity if it isn't unlocked yet
     **/
    storyNodeUnlockedTime(storyNode) {
        return this._storyPlayer.storyNodeUnlockedTime(storyNode);
    }

    /**
     * Returns the time that a StoryNode was available
     * @param {StoryNode} storyNode
     * @return {number}  The value of the time at the available of the StoryNode or Infinity if it isn't available at the moment
     **/
    storyNodeAvailableTime(storyNode) {
        return this._storyPlayer.storyNodeAvailableTime(storyNode);
    }

    /**
     * Returns the time that a StoryNode was completed
     * @param {StoryNode} storyNode
     * @return {number}  The value of the time at the unlock of the StoryNode or Infinity if it isn't completed yet
     **/
    storyNodeCompletedTime(storyNode) {
        return this._storyPlayer.storyNodeCompletedTime(storyNode);
    }

    /**
     * Returns the time that a StoryArc was unlocked
     * @param {StoryArc} storyArc
     * @return {number}  The value of the time at the unlock of the StoryArc or Infinity if it isn't unlocked yet
     **/
    storyArcUnlockedTime(storyArc) {
        return this._storyPlayer.storyArcUnlockedTime(storyArc);
    }

    /**
     * Returns the time that a StoryArc was available
     * @param {StoryArc} storyArc
     * @return {number} The value of the time at the available of the StoryArc or Infinity if it isn't available at the moment
     **/
    storyArcAvailableTime(storyArc) {
        return this._storyPlayer.storyArcAvailableTime(storyArc);
    }

    /**
     * Returns the time that a StoryArc was completed
     * @param {StoryArc} storyArc
     * @return {number} The value of the time at the complete of the StoryArc or Infinity if it isn't completed yet
     **/
    storyArcCompletedTime(storyArc) {
        return this._storyPlayer.storyArcCompletedTime(storyArc);
    }

    /**
     * Returns the time that the StoryPlayer was started
     * @return {number} The value of the time at the start of the StoryPlayer or Infinity if it isn't started yet
     **/
    getStoryStartTime() {
        return this._storyPlayer.getStoryStartTime();
    }

    /**
     * Get the result of a StoryNode that was stored upon completion of the StoryNode. If the
     * StoryNode was not completed yet null is returned. Note that null is also returned when
     * no result is set so don't use a null return as an indicator that the storyNode was not completed, use
     * isStoryNodeCompleted for this.
     * @param {StoryNode} storyNode The StoryNode to get the result from
     * @return {*} The result of the storynode that was passed with the complete StoryFrameworkPlayer.prototype.of the storyNode
     **/
    getResult(storyNode) {
        return this._storyPlayer.getResult(storyNode);
    }

    /**
     * Return the story the IStoryPlayer is playing
     * @return {Story} The Story
     **/
    getStory() {
        return this._storyPlayer.getStory();
    }

    /**
     * Load the state of the player based on a memento =save of the state, see the technical
     * design on the background of this pattern.
     * @storyPlayerMememento
     * @param {StoryFrameworkPlayerMemento} storyFrameworkPlayerMememento The memento to load
     **/
    load({ _storyPlayerMemento }) {
        this._storyPlayer.load(_storyPlayerMemento);
        this._conditionSolver.reset();
    }

    /**
     * Get a memento object of the current state of the player that can be loaded later on so
     * the state can be restored. 
     * @return {StoryFrameworkPlayerMemento} A StoryFrameworkPlayerMemento that can be loaded to restore the state of the StoryPlayer
     **/
    save() {
        return new StoryFrameworkPlayerMemento(this._storyPlayer.save());
    }

    /**
     * @private
     * @returns {undefined}
     */
    addEventListeners() {
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_START, this.onStoryStart, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_END, this.onStoryEnd, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_ARC_AVAILABLE, this.onStoryArcAvailable, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_ARC_UNAVAILABLE, this.onStoryArcUnavailable, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_ARC_UNLOCKED, this.onStoryArcUnlocked, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_ARC_COMPLETED, this.onStoryArcCompleted, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_NODE_AVAILABLE, this.onStoryNodeAvailable, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_NODE_UNAVAILABLE, this.onStoryNodeUnavailable, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_NODE_UNLOCKED, this.onStoryNodeUnlocked, this);
        this._storyPlayer.addEventListener(StoryPlayerEvent.STORY_NODE_COMPLETED, this.onStoryNodeCompleted, this);
    }

    /**
     * @private
     * @returns {undefined}
     */
    removeEventListeners() {
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_START, this.onStoryStart);
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_ARC_AVAILABLE, this.onStoryArcAvailable);
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_ARC_UNAVAILABLE, this.onStoryArcUnavailable);
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_ARC_UNLOCKED, this.onStoryArcUnlocked);

        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_NODE_AVAILABLE, this.onStoryNodeAvailable);
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_NODE_UNAVAILABLE, this.onStoryNodeUnavailable);
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_NODE_UNLOCKED, this.onStoryNodeUnlocked);
        this._storyPlayer.removeEventListener(StoryPlayerEvent.STORY_NODE_COMPLETED, this.onStoryNodeCompleted);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryStart(e) {
        this.dispatchEvent(StoryFrameworkPlayerEvent.STORY_START);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryEnd(e) {
        this.dispatchEvent(StoryFrameworkPlayerEvent.STORY_END);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryArcAvailable({ data }) {
        this.dispatchEvent(StoryFrameworkPlayerEvent.STORY_ARC_AVAILABLE, data);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryArcUnavailable({ data }) {
        this.dispatchEvent(StoryFrameworkPlayerEvent.STORY_ARC_UNAVAILABLE, data);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryArcCompleted({ data }) {
        this.dispatchEvent(StoryFrameworkPlayerEvent.STORY_ARC_COMPLETED, data);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryArcUnlocked({ data }) {
        this.dispatchEvent(StoryFrameworkPlayerEvent.STORY_ARC_UNLOCKED, data);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryNodeUnlocked({ data }) {
        this.dispatchEvent(StoryFrameworkPlayerEvent.STORY_NODE_UNLOCKED, data);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryNodeAvailable({ data }) {
        this.dispatchEvent(StoryFrameworkPlayerEvent.STORY_NODE_AVAILABLE, data);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryNodeUnavailable({ data }) {
        this.dispatchEvent(StoryFrameworkPlayerEvent.STORY_NODE_UNAVAILABLE, data);
    }

    /**
     * @private
     * @param {Event} e
     * @returns {undefined}
     */
    onStoryNodeCompleted({ data }) {
        this.dispatchEvent(StoryFrameworkPlayerEvent.STORY_NODE_COMPLETED, data);
    }
}

